import React from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import type { ThemeAIUploadInspection } from '../../types/gotypes'

export interface UploadColumns {
  textColumn: string
  idColumn: string
}

interface UploadColumnPickerProps {
  fileName: string
  inspection: ThemeAIUploadInspection
  value: UploadColumns
  onChange: (value: UploadColumns) => void
}

// After a file is inspected, the user confirms which column holds the answers
// to code and, optionally, which one identifies the respondent. The server
// suggests the column with the longest answers; the samples underneath show
// exactly what will be coded, so a wrong guess is obvious before anything is
// imported.
const UploadColumnPicker: React.FC<UploadColumnPickerProps> = ({ fileName, inspection, value, onChange }) => {
  const textColumn = inspection.columns.find((column) => column.name === value.textColumn)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        <strong>{fileName}</strong> · {inspection.row_count.toLocaleString()} rows · {inspection.columns.length} columns
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="themeai-text-column">Responses to code</InputLabel>
          <Select
            labelId="themeai-text-column"
            label="Responses to code"
            value={value.textColumn}
            onChange={(event) => onChange({ ...value, textColumn: event.target.value })}
          >
            {inspection.columns.map((column) => (
              <MenuItem key={column.name} value={column.name} disabled={column.name === value.idColumn}>
                {column.name}
                {column.name === inspection.suggested_text_column ? ' (suggested)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id="themeai-id-column">Respondent ID (optional)</InputLabel>
          <Select
            labelId="themeai-id-column"
            label="Respondent ID (optional)"
            value={value.idColumn}
            onChange={(event) => onChange({ ...value, idColumn: event.target.value })}
          >
            <MenuItem value="">None — number the rows</MenuItem>
            {inspection.columns.map((column) => (
              <MenuItem key={column.name} value={column.name} disabled={column.name === value.textColumn}>
                {column.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {textColumn && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            First answers in “{textColumn.name}” · {textColumn.non_empty.toLocaleString()} not empty
          </Typography>
          <Box component="ol" sx={{ m: 0, mt: 0.5, p: 0, listStyle: 'none' }}>
            {textColumn.samples.map((sample, index) => (
              <Typography
                component="li"
                key={index}
                variant="body2"
                noWrap
                title={sample}
                sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider', color: sample ? 'text.primary' : 'text.disabled' }}
              >
                {sample || '(empty)'}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default UploadColumnPicker
